/*
 * LocationInput — shared component used by Islamic tools (prayer-times, qibla-finder).
 *
 * Two tabs: (1) city search with autocomplete, (2) geolocation + manual lat/lng.
 * Data: /data/cities/index.json + /data/cities/names-{locale}.json (lazy on mount).
 *
 * Public API:
 *   LocationInput.mount(rootEl, {
 *       tool:    'prayer-times' | 'qibla-finder',
 *       initial: { lat, lng, cityName, tz },
 *       t:       { [translation key]: string },
 *       onChange: ({ lat, lng, tz, cityName, source }) => void,
 *       defaultTab: 'city' | 'location'
 *   });
 */
(function(){
'use strict';

var LOCALES = ['en','ru','ko','ar','id','pt','de','es','fr','uk','tr'];
var FLAG_BASE = 0x1F1A5; // regional indicator base minus 'A'

/* ── Unicode normalization for fuzzy matching ── */
function normalize(str){
    if (!str) return '';
    var s = String(str).normalize('NFKD').toLowerCase();
    /* strip combining marks (Latin diacritics, Arabic harakat) */
    s = s.replace(/[\u0300-\u036f\u0610-\u061a\u064b-\u065f\u0670\u06d6-\u06ed]/g, '');
    /* Arabic alef / alef-madda / alef-hamza → plain alef; yaa-maksura → yaa; ta-marbuta → haa */
    s = s.replace(/[\u0622\u0623\u0625]/g, '\u0627')
         .replace(/\u0649/g, '\u064a')
         .replace(/\u0629/g, '\u0647');
    /* Apostrophes in GeoNames asciiNames are transliteration artefacts of the
       Russian soft sign \u2014 "Shlissel'burg", "Podporozh'ye". Nobody types them, and
       the matcher is substring-based, so they made those cities unreachable by
       their own Latin name. Dropped on BOTH sides (index + query), so a user who
       does type one still matches. */
    s = s.replace(/['\u2019\u02bc\u02b9]/g, '');
    return s;
}

/* The country printed next to a place — '' where its country is disputed
   (Крым, Донецк, Сухум, Приднестровье, Косово): no flag, no code. The table and
   the rule live in partials/geo-disputed (App\Services\Geo\DisputedPlaces). */
function shownCc(city){
    if (!city || !city.cc) return '';
    return window.__geoShownCc ? window.__geoShownCc(city.cc, city.region) : city.cc;
}

function flagEmoji(cc){
    if (!cc || cc.length !== 2) return '';
    var a = cc.toUpperCase().charCodeAt(0);
    var b = cc.toUpperCase().charCodeAt(1);
    if (a < 65 || a > 90 || b < 65 || b > 90) return '';
    return String.fromCodePoint(0x1F1E6 + (a - 65), 0x1F1E6 + (b - 65));
}


/* ── Data loader (module-scoped cache) ── */
var _dataPromise = null;
var _data = null; // { index: [...], names: [...], normIndex: [...] }

function currentLocale(){
    var l = (window.__LOCALE || 'en').toLowerCase();
    return LOCALES.indexOf(l) === -1 ? 'en' : l;
}

function loadData(){
    if (_data) return Promise.resolve(_data);
    if (_dataPromise) return _dataPromise;
    var loc = currentLocale();
    var v = window.__CITIES_V__ || '';
    var qs = v ? ('?v=' + v) : '';
    /* Only 2 files per user: index (shared) + names for the active locale.
       Latin search works via row[4] (asciiName column inside index.json). */
    _dataPromise = Promise.all([
        /* index is essential — a non-2xx (404 / stale-deploy / HTML error page) must REJECT,
           not feed an HTML body into r.json() and poison the cache. */
        fetch('/data/cities/index.json' + qs, { credentials: 'omit' }).then(function(r){
            if (!r.ok) throw new Error('cities index HTTP ' + r.status);
            return r.json();
        }),
        /* names file is OPTIONAL — a 404 (locale not built) must not kill the picker;
           search/rowToCity gracefully fall back to the asciiName column (row[4]). */
        fetch('/data/cities/names-' + loc + '.json' + qs, { credentials: 'omit' }).then(function(r){
            return r.ok ? r.json() : [];
        }).catch(function(){ return []; })
    ]).then(function(res){
        var index = res[0];
        var localNames = res[1] || [];
        if (!Array.isArray(index) || !index.length) throw new Error('cities index empty');
        /* Index↔names length skew (a stale-deploy mismatch where names didn't re-sync with
           index) would map names to the WRONG rows — drop to ascii rather than show/save wrong. */
        if (localNames.length && localNames.length !== index.length) localNames = [];
        var normIndex = new Array(index.length);
        for (var i = 0; i < index.length; i++){
            var ascii = index[i][4] || '';
            var lname = localNames[i] || '';
            normIndex[i] = {
                ascii: normalize(ascii),
                local: lname ? normalize(lname) : ''
            };
        }
        _data = { index: index, names: localNames, normIndex: normIndex };
        try { if (window.__ae) window.__ae('cities_load_ok', { count: index.length, loc: loc }); } catch(e){}
        return _data;
    }).catch(function(err){
        /* CRITICAL: null the memoized promise so a later focus/keystroke RE-FETCHES instead of
           replaying the cached rejection for the whole session (the poisoned-promise bug). */
        _dataPromise = null;
        try { if (window.__ae) window.__ae('cities_load_fail', { err: String((err && err.message) || err).slice(0,120), loc: loc }); } catch(e){}
        throw err;
    });
    return _dataPromise;
}

/* ── RU small-town supplement (lazy) ──
   /data/cities/ru-extra.json carries ~4 000 Russian towns of 500–15 000 people:
   the district centres and settlements people actually live in (Шлиссельбург,
   Мга, Синявино, Имени Морозова) that the shared cities15000 threshold cuts.
   Merging them into index.json would cost EVERY user of every geo tool ~12%
   more payload for one country's benefit, so the file is fetched lazily — only
   after a search has already come up empty, and at most once per session.
   Whoever finds their city on the first try never downloads it.

   Rows are [name, lat, lng, pop, geonameid, tz, region, asciiName] and are
   projected onto the shared 8-column row shape, so once merged they are
   indistinguishable to search / rowToCity / nearestByCoords. */
var _extraPromise = null, _extraLoaded = false, _extraFails = 0;

function loadRuSupplement(){
    if (_extraLoaded || !_data) return Promise.resolve(false);
    if (_extraPromise) return _extraPromise;
    if (_extraFails >= 2) return Promise.resolve(false);   // stop retrying a broken/absent file
    var v = window.__CITIES_V__ || '';
    _extraPromise = fetch('/data/cities/ru-extra.json' + (v ? '?v=' + v : ''), { credentials: 'omit' })
        .then(function(r){
            if (!r.ok) throw new Error('ru-extra HTTP ' + r.status);
            return r.json();
        })
        .then(function(j){
            var rows = (j && j.cities) || [];
            var have = {};
            for (var s = 0; s < _data.index.length; s++){
                var gid = _data.index[s][6];
                if (gid) have[gid] = 1;
            }
            var added = 0;
            for (var k = 0; k < rows.length; k++){
                var e = rows[k];
                if (!e || !e[0] || have[e[4]]) continue;
                /* Pre-2026-07-28 builds of this file have no tz/region/ascii —
                   fall back to the Cyrillic name so the row is still searchable. */
                var ascii = e[7] || '';
                var i0 = _data.index.length;
                _data.index.push([e[1], e[2], 'RU', e[5] || '', ascii || e[0], e[3] || 0, e[4], e[6] || '']);
                _data.names[i0] = e[0];
                _data.normIndex[i0] = { ascii: normalize(ascii), local: normalize(e[0]) };
                added++;
            }
            _extraLoaded = true;
            try { if (window.__ae) window.__ae('cities_extra_ok', { added: added }); } catch(e){}
            return added > 0;
        })
        .catch(function(err){
            _extraPromise = null;
            _extraFails++;
            try { if (window.__ae) window.__ae('cities_extra_fail', { err: String((err && err.message) || err).slice(0, 120) }); } catch(e){}
            return false;
        });
    return _extraPromise;
}

/* Search, then — only if it found nothing — pull the supplement and retry.
   Callers that can await get the wider base for free; the sync search() stays
   untouched for anything that can't. */
function searchWithFallback(query, limit){
    var hits = search(query, limit);
    if (hits.length) return Promise.resolve(hits);
    return loadRuSupplement().then(function(added){ return added ? search(query, limit) : []; });
}

function findByNameWithFallback(name, cc){
    var hit = findByName(name, cc);
    if (hit) return Promise.resolve(hit);
    return loadRuSupplement().then(function(added){ return added ? findByName(name, cc) : null; });
}

/* ── Typed, but not picked from the list ──
   What the text in the box means on its own, for the moment a form is sent.
   Most people type the name and press the button: on /synastry 202 of 1980
   sessions (14 days to 2026-10-16) got «choose a birth place» while the list
   under the field held exactly the city they had typed, and 44 of them left.
   The rule is natal-chart's (resolveTypedPlace): one candidate, or one
   clearly biggest among same-named ones (×8 population), is taken — the
   status line then names it; real ambiguity goes back to the person as the
   list, never as a silent guess.
   → { state: 'picked', city } | { state: 'ambiguous', pool } | { state: 'unknown' }
     | { state: 'unavailable' } — the city index did not load, nothing can be said */
function resolveTyped(query){
    var q = String(query == null ? '' : query).trim();
    if (q.length < 2) return Promise.resolve({ state: 'unknown', query: q });
    return loadData().catch(function(){ return null; }).then(function(){
        return _data ? searchWithFallback(q, 15) : null;
    }).then(function(hits){
        if (hits === null) return { state: 'unavailable', query: q };
        if (!hits.length) return { state: 'unknown', query: q };
        var wanted = {};
        placeQueryVariants(q).forEach(function(v){ wanted[normalize(v)] = 1; });
        var cities = hits.map(function(h){ return rowToCity(h.i); });
        var exact = cities.filter(function(c){ return wanted[normalize(c.name)] || wanted[normalize(c.asciiName)]; });
        var pool = (exact.length ? exact : cities).slice()
            .sort(function(a, b){ return (b.pop || 0) - (a.pop || 0); });
        var top = pool[0], rival = pool[1];
        if (!rival || (top.pop || 0) >= (rival.pop || 0) * 8) return { state: 'picked', query: q, city: top };
        return { state: 'ambiguous', query: q, pool: pool };
    });
}

/* ── Settlement-type words ──
   People type the administrative type in front of (or after) the name:
   "город Отрадное", "г. Отрадное", "пос. Мга", "city of Springfield". The
   dataset stores the BARE name ("Отрадное"), and matching here is substring-
   based, so the raw query scores zero and the picker funnels the user into
   "suggest a city" — for a city we already have. Three of the four
   city-suggestions ever filed were exactly this (Челябинск, Оренбург,
   "город Отрадное"), zero were genuinely missing cities.

   Stripping is a RETRY-ON-MISS: the raw query is always tried first, so an
   over-eager strip can only ADD candidates, never hide a correct match. The
   long forms require a following space ("Городец" survives, "город X" strips)
   and the one-letter abbreviations require a dot ("г. X", never "гXYZ"). */
var PLACE_TYPE_LONG = 'город|гор|посёлок|поселок|пос|пгт|село|деревня|дер|станица|хутор|аул|микрорайон|мкр|the city of|city of|town of|village of';
var PLACE_TYPE_ABBR = 'г|п|с|д|х|ст';
var PLACE_PREFIX_RE = new RegExp('^(?:(?:' + PLACE_TYPE_LONG + ')(?:\\.\\s*|\\s+)|(?:' + PLACE_TYPE_ABBR + ')\\.\\s*)', 'i');
var PLACE_SUFFIX_RE = new RegExp('\\s+(?:' + PLACE_TYPE_LONG + '|' + PLACE_TYPE_ABBR + ')\\.?$', 'i');

/* "город Отрадное" → "Отрадное". Returns the input unchanged when nothing was
   stripped, or when stripping would leave less than a searchable stub. */
function stripPlaceType(str){
    var s = String(str == null ? '' : str).trim();
    var out = s.replace(PLACE_PREFIX_RE, '').replace(PLACE_SUFFIX_RE, '').trim();
    return out.length >= 2 ? out : s;
}

/* Ordered, deduped list of things worth matching for a user-typed place:
   the raw text first, then the type-stripped form, then the part before a
   comma ("Отрадное, Ленинградская область") and its stripped form. */
function placeQueryVariants(query){
    var out = [], seen = {};
    function push(s){
        s = String(s == null ? '' : s).trim();
        var k = s.toLowerCase();
        if (s.length >= 2 && !seen[k]){ seen[k] = 1; out.push(s); }
    }
    var raw = String(query == null ? '' : query).trim();
    push(raw);
    /* No city name contains a comma, so anything after one is a region tail. */
    var comma = raw.indexOf(',');
    var head = comma > 0 ? raw.slice(0, comma).trim() : raw;
    if (head !== raw) push(head);
    push(stripPlaceType(head));
    return out;
}

/* ── Search ── */
function search(query, limit){
    var variants = placeQueryVariants(query);
    for (var v = 0; v < variants.length; v++){
        var hits = searchExact(variants[v], limit);
        if (hits.length) return hits;
    }
    return [];
}

function searchExact(query, limit){
    if (!_data) return [];
    var q = normalize(query);
    if (!q) return [];
    var results = [];
    var idx = _data.index;
    var ni = _data.normIndex;
    for (var i = 0; i < idx.length; i++){
        var entry = ni[i];
        var score = 0;
        /* Match against ascii + local. Latin search works via asciiName even on non-en locales. */
        var candidates = [entry.ascii, entry.local];
        for (var c = 0; c < candidates.length; c++){
            var cand = candidates[c];
            if (!cand) continue;
            if (cand === q)           { score = Math.max(score, 5); continue; }
            if (cand.indexOf(q) === 0){ score = Math.max(score, 4); continue; }
            /* word-boundary match shares the prefix tier (4) so the population tiebreak promotes
               the larger city: "york"→New York (8.8M) over tiny York; "delhi"→New Delhi, etc. */
            var spaceHit = cand.indexOf(' ' + q);
            if (spaceHit !== -1)      { score = Math.max(score, 4); continue; }
            if (cand.indexOf(q) !== -1){ score = Math.max(score, 1); }
        }
        if (score > 0){
            results.push({ i: i, score: score, pop: idx[i][5] });
        }
    }
    results.sort(function(a, b){
        if (b.score !== a.score) return b.score - a.score;
        return b.pop - a.pop;
    });
    return results.slice(0, limit || 15);
}

function rowToCity(i){
    var row = _data.index[i];
    var name = _data.names[i] || row[4];
    return {
        index:    i,
        lat:      row[0],
        lng:      row[1],
        cc:       row[2],
        tz:       row[3],
        ascii:    row[4],
        pop:      row[5],
        name:     name,
        asciiName: row[4],
        geonameid: row[6] || null,   // stable id (added 2026-06-05; null on legacy rows)
        region:    row[7] || ''      // admin1 region name (ASCII)
    };
}

/* Reverse geocode: nearest city to a lat/lng from the loaded base. Uses a fast
   equirectangular approximation (fine at city scale). To avoid returning an
   obscure hamlet next door, a populous neighbour (≥50k) within ~45 km wins over
   the literal nearest dot — so device coords resolve to "Berlin", not a suburb. */
function nearestByCoords(lat, lng){
    if (!_data || lat == null || lng == null) return null;
    var idx = _data.index;
    var rad = Math.PI / 180, cosLat = Math.cos(lat * rad);
    var bestNear = -1, bestNearD = Infinity;
    var bestPop = -1, bestPopVal = -1, bestPopD = Infinity;
    for (var i = 0; i < idx.length; i++){
        var dlat = (idx[i][0] - lat);
        var dlng = (idx[i][1] - lng) * cosLat;
        var d = (dlat * dlat + dlng * dlng) * 12347.65; // (×111.32²) → km²
        if (d < bestNearD){ bestNearD = d; bestNear = i; }
        if (d <= 2025){ // within ~45 km
            var pop = idx[i][5] || 0;
            if (pop >= 50000 && pop > bestPopVal){ bestPopVal = pop; bestPop = i; bestPopD = d; }
        }
    }
    var pick = bestPop !== -1 ? bestPop : bestNear;
    return pick === -1 ? null : rowToCity(pick);
}

function findByName(name, cc){
    if (!name || !_data) return null;
    /* Same retry-on-miss as search(): "г. Отрадное" must resolve to the
       existing Отрадное instead of filing a duplicate suggestion. */
    var variants = placeQueryVariants(name);
    for (var v = 0; v < variants.length; v++){
        var hit = findByExactName(variants[v], cc);
        if (hit) return hit;
    }
    return null;
}

function findByExactName(name, cc){
    if (!name || !_data) return null;
    var n = normalize(name);
    if (!n) return null;   // a combining-mark-only / whitespace query normalizes to '' — never match row 0
    cc = cc ? String(cc).toUpperCase() : null;
    for (var i = 0; i < _data.index.length; i++){
        if (cc && _data.index[i][2] !== cc) continue;          // country filter (when caller knows the cc)
        var nm = _data.names[i];
        if ((nm && normalize(nm) === n) || normalize(_data.index[i][4] || '') === n) return rowToCity(i);
    }
    return null;
}

/* ── DOM helpers ── */
function el(tag, attrs, children){
    var e = document.createElement(tag);
    if (attrs){
        for (var k in attrs){
            if (k === 'class') e.className = attrs[k];
            else if (k === 'text') e.textContent = attrs[k];
            else if (k === 'html') e.innerHTML = attrs[k];
            else e.setAttribute(k, attrs[k]);
        }
    }
    if (children){
        for (var i = 0; i < children.length; i++){
            if (children[i] != null) e.appendChild(children[i]);
        }
    }
    return e;
}

/* ── UI ── */
function mount(root, opts){
    if (!root) return null;
    opts = opts || {};
    var t = opts.t || {};
    var initial = opts.initial || {};
    var onChange = opts.onChange || function(){};

    /* Profile-location fallback: a logged-in user with a saved city gets it auto-applied to ANY
       location tool (prayer-times/qibla/distance + future) when the tool has no own saved location.
       window.__USER_LOCATION is set authed-only by the layout (so it is never page-cached). Single
       shared source — no tool hardcodes a location. */
    var _usedProfileLoc = false;
    if (initial.lat == null && !initial.cityName && window.__USER_LOCATION && window.__USER_LOCATION.lat != null){
        initial = {
            lat: window.__USER_LOCATION.lat,
            lng: window.__USER_LOCATION.lng,
            tz: window.__USER_LOCATION.tz || null,
            cityName: window.__USER_LOCATION.cityName || null
        };
        _usedProfileLoc = true;
    }

    function tr(key, fallback){ return (t[key] != null ? t[key] : fallback); }

    root.classList.add('loc-input');
    root.innerHTML = '';

    /* Tabs */
    var tabs = el('div', { class: 'loc-tabs', role: 'tablist' });
    var tabCity = el('button', { class: 'loc-tab active', type: 'button', role: 'tab', 'aria-selected': 'true',
        text: '🏙 ' + tr('tab_city', 'City') });
    var tabGeo  = el('button', { class: 'loc-tab', type: 'button', role: 'tab', 'aria-selected': 'false',
        text: '📍 ' + tr('tab_location', 'Location') });
    tabs.appendChild(tabCity); tabs.appendChild(tabGeo);
    root.appendChild(tabs);

    var panelCity = el('div', { class: 'loc-panel loc-panel-city active', role: 'tabpanel' });
    var panelGeo  = el('div', { class: 'loc-panel loc-panel-geo',  role: 'tabpanel' });
    root.appendChild(panelCity);
    root.appendChild(panelGeo);

    /* ── City panel ── */
    var searchInput = el('input', {
        class: 'loc-search',
        type: 'search',
        autocomplete: 'off',
        autocapitalize: 'off',
        spellcheck: 'false',
        placeholder: tr('city_search_placeholder', 'Start typing your city…'),
        'aria-label': tr('city_search_placeholder', 'Start typing your city…')
    });
    var searchHint = el('div', { class: 'loc-search-hint', text: tr('city_search_hint', '') });
    var resultsBox = el('div', { class: 'loc-results', role: 'listbox' });
    var statusCity = el('div', { class: 'loc-status' });
    panelCity.appendChild(searchInput);
    panelCity.appendChild(searchHint);
    panelCity.appendChild(resultsBox);
    panelCity.appendChild(statusCity);

    /* ── Geo panel ── */
    var btnGeo = el('button', { class: 'loc-btn', type: 'button', text: '📍 ' + tr('use_location', 'Use my location') });
    var manual = el('div', { class: 'loc-manual' });
    var latLbl = el('label', { class: 'loc-label' });
    latLbl.appendChild(document.createTextNode(tr('latitude', 'Latitude')));
    var latInp = el('input', { type: 'number', step: '0.0001', min: '-90', max: '90', placeholder: '40.7128' });
    latLbl.appendChild(latInp);
    var lngLbl = el('label', { class: 'loc-label' });
    lngLbl.appendChild(document.createTextNode(tr('longitude', 'Longitude')));
    var lngInp = el('input', { type: 'number', step: '0.0001', min: '-180', max: '180', placeholder: '-74.0060' });
    lngLbl.appendChild(lngInp);
    var setBtn = el('button', { class: 'loc-btn secondary', type: 'button', text: tr('set_location', 'Set location') });
    manual.appendChild(latLbl); manual.appendChild(lngLbl); manual.appendChild(setBtn);
    var geoHint = el('div', { class: 'loc-ios-hint', text: tr('location_ios_hint', '') });
    var statusGeo = el('div', { class: 'loc-status' });
    panelGeo.appendChild(btnGeo);
    panelGeo.appendChild(manual);
    panelGeo.appendChild(geoHint);
    panelGeo.appendChild(statusGeo);

    /* Tab switching */
    function activateTab(which){
        var isCity = which === 'city';
        tabCity.classList.toggle('active', isCity);
        tabGeo.classList.toggle('active', !isCity);
        tabCity.setAttribute('aria-selected', isCity ? 'true' : 'false');
        tabGeo.setAttribute('aria-selected', isCity ? 'false' : 'true');
        panelCity.classList.toggle('active', isCity);
        panelGeo.classList.toggle('active', !isCity);
        if (isCity){
            loadData().catch(function(err){
                statusCity.classList.add('error');
                statusCity.textContent = tr('city_load_error', 'Cities database failed to load.');
            });
            setTimeout(function(){ searchInput.focus(); }, 50);
        }
    }
    tabCity.addEventListener('click', function(){ activateTab('city'); });
    tabGeo .addEventListener('click', function(){ activateTab('geo'); });

    /* Search render */
    function renderStatus(box, text, isError){
        box.textContent = text || '';
        box.classList.toggle('error', !!isError);
    }

    /* The text that is backed by a pick. Anything else in the box is typed and
       not yet a place — see resolvePending below. */
    var committed = '';

    function emitCity(city){
        renderStatus(statusCity, city.name + ', ' + [city.region, shownCc(city)].filter(Boolean).join(', ') + ' · ' + city.tz);
        resultsBox.innerHTML = '';
        resultsBox.classList.remove('open');
        searchInput.value = city.name;
        committed = city.name;
        try { if (window.__ae) window.__ae('cities_select', { tool: opts.tool || '', geonameid: city.geonameid || null, cc: city.cc }); } catch(e){}
        onChange({ lat: city.lat, lng: city.lng, tz: city.tz, cityName: city.name, source: 'city' });
    }

    function renderResults(list){
        resultsBox.innerHTML = '';
        if (!list.length){
            resultsBox.classList.remove('open');
            return;
        }
        resultsBox.classList.add('open');
        list.forEach(function(r){
            var city = rowToCity(r.i);
            var item = el('button', {
                class: 'loc-result',
                type: 'button',
                role: 'option',
                'data-idx': String(r.i)
            });
            var flag = el('span', { class: 'loc-flag', text: flagEmoji(shownCc(city)) });
            var label = el('span', { class: 'loc-result-name', text: city.name });
            var meta = el('span', { class: 'loc-result-meta', text: [city.region, shownCc(city)].filter(Boolean).join(', ') + ' · ' + city.tz });
            item.appendChild(flag); item.appendChild(label); item.appendChild(meta);
            item.addEventListener('click', function(){ emitCity(city); });
            resultsBox.appendChild(item);
        });
    }

    var debounceT = null;
    function doSearch(){
        var q = searchInput.value.trim();
        if (q.length < 2){ resultsBox.innerHTML = ''; resultsBox.classList.remove('open'); return; }
        if (!_data){
            loadData().then(doSearch).catch(function(){
                renderStatus(statusCity, tr('city_load_error', 'Could not load cities — please retry.'), true);
            });
            return;
        }
        searchWithFallback(q, 15).then(function(results){
            /* A later keystroke may have already moved on — don't paint stale hits. */
            if (searchInput.value.trim() !== q) return;
            try { if (window.__ae) window.__ae('cities_search', { tool: opts.tool || '', hadResults: results.length > 0 }); } catch(e){}
            if (!results.length){ renderEmpty(); return; }
            renderResults(results);
        });
    }
    function renderEmpty(){
        resultsBox.innerHTML = '';
        resultsBox.classList.add('open');
        resultsBox.appendChild(el('div', { class: 'loc-result-empty', text: tr('city_no_results', 'No results') }));
    }
    searchInput.addEventListener('input', function(){
        clearTimeout(debounceT);
        debounceT = setTimeout(doSearch, 100);
    });
    searchInput.addEventListener('focus', function(){
        /* Kick off data load on first focus (preload may already have started it).
           The refusal is shown where people look (doSearch / the city tab); this is
           only a warm-up — without the catch a failed fetch (a 403 from the user's
           own proxy, TIMBRICA-FRONTEND-J8: zero 403s in our logs) became an
           unhandledrejection and a generic «something broke» toast on top of the
           honest status line. Guard: LoadDataRejectionIsAlwaysHandledTest. */
        if (!_data) loadData().catch(function(){});
    });
    document.addEventListener('click', function(ev){
        if (!panelCity.contains(ev.target)) resultsBox.classList.remove('open');
    });

    /* Keyboard: arrow nav on results */
    searchInput.addEventListener('keydown', function(ev){
        var items = resultsBox.querySelectorAll('.loc-result');
        if (!items.length) return;
        if (ev.key === 'ArrowDown'){ ev.preventDefault(); items[0].focus(); }
        else if (ev.key === 'Enter'){ ev.preventDefault(); items[0].click(); }
    });
    resultsBox.addEventListener('keydown', function(ev){
        var items = Array.prototype.slice.call(resultsBox.querySelectorAll('.loc-result'));
        var idx = items.indexOf(document.activeElement);
        if (ev.key === 'ArrowDown'){ ev.preventDefault(); if (items[idx+1]) items[idx+1].focus(); }
        else if (ev.key === 'ArrowUp'){ ev.preventDefault(); if (idx <= 0) searchInput.focus(); else items[idx-1].focus(); }
        else if (ev.key === 'Escape'){ resultsBox.classList.remove('open'); searchInput.focus(); }
    });

    /* ── Geolocation ── */
    btnGeo.addEventListener('click', function(){
        renderStatus(statusGeo, tr('loading_location', 'Requesting…'));
        if (!navigator.geolocation){
            // The primitive names the real cause (a browser setting, not a
            // missing feature) and reports it; the inline status stays short.
            var exp = window.__geo ? __geo.notify('no_api') : null;
            renderStatus(statusGeo, exp ? exp.title : tr('location_error', 'Geolocation unavailable'), true);
            return;
        }
        navigator.geolocation.getCurrentPosition(function(pos){
            var lat = pos.coords.latitude, lng = pos.coords.longitude;
            renderStatus(statusGeo, lat.toFixed(4) + ', ' + lng.toFixed(4));
            latInp.value = lat.toFixed(4);
            lngInp.value = lng.toFixed(4);
            /* No IANA tz on geolocation — tool falls back to browser offset */
            onChange({ lat: lat, lng: lng, tz: null, cityName: null, source: 'geo' });
        }, function(err){
            var isDenied = err && err.code === err.PERMISSION_DENIED;
            renderStatus(statusGeo, isDenied ? tr('location_denied', 'Location denied') : tr('location_error', 'Location error'), true);
            /* Hint the user toward Tab 1 */
            geoHint.classList.add('visible');
        }, { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 });
    });

    setBtn.addEventListener('click', function(){
        var lat = parseFloat(latInp.value);
        var lng = parseFloat(lngInp.value);
        if (!isFinite(lat) || !isFinite(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180){
            renderStatus(statusGeo, tr('location_error', 'Invalid coordinates'), true);
            return;
        }
        renderStatus(statusGeo, lat.toFixed(4) + ', ' + lng.toFixed(4));
        onChange({ lat: lat, lng: lng, tz: null, cityName: null, source: 'manual' });
    });

    /* ── Initial state ── */
    if (initial.cityName){
        searchInput.value = initial.cityName;
        committed = initial.cityName;
        renderStatus(statusCity, initial.cityName + (initial.tz ? ' · ' + initial.tz : ''));
    } else if (initial.lat != null && initial.lng != null){
        activateTab('geo');
        latInp.value = initial.lat;
        lngInp.value = initial.lng;
        renderStatus(statusGeo, (+initial.lat).toFixed(4) + ', ' + (+initial.lng).toFixed(4));
    }

    /* If the location came from the user's profile (not the tool's own saved state), tell the tool
       so it actually applies it — compute prayer times / qibla, pre-fill the endpoint, etc. */
    if (_usedProfileLoc && initial.lat != null){
        onChange({ lat: initial.lat, lng: initial.lng, tz: initial.tz, cityName: initial.cityName, source: 'profile' });
    }

    if (opts.defaultTab === 'location') activateTab('geo');

    /* Preload strategy:
       - If user already has a saved location (initial.lat set) → don't prefetch; data only
         loads when they actively click the search input. Saves ~800 KB for return visits.
       - Fresh visitor with no saved location → preload so the first keystroke is instant.
       - saveData (Data Saver) respects the user's bandwidth preference and always lazy-loads. */
    var saveData = navigator.connection && navigator.connection.saveData;
    var hasSavedLocation = initial && initial.lat != null && initial.lng != null;
    if (!saveData && !hasSavedLocation && panelCity.classList.contains('active')){
        loadData().catch(function(){ /* swallow — will retry on focus */ });
    }

    return {
        setExternal: function(lat, lng, tz, cityName){
            if (cityName){ searchInput.value = cityName; committed = cityName; }
            if (lat != null) latInp.value = (+lat).toFixed(4);
            if (lng != null) lngInp.value = (+lng).toFixed(4);
        },
        findByName: function(n){ return findByName(n); },
        /* For a form's submit: turn text typed in the city box but never picked
           into a place (onChange fires as for a click), or show the person the
           list to choose from. Also catches the stale case — a city picked,
           then the text rewritten: the tool still held the OLD coordinates.
           → Promise<{ state: 'none' | 'picked' | 'ambiguous' | 'unknown', query? }> */
        resolvePending: function(){
            var q = searchInput.value.trim();
            if (!panelCity.classList.contains('active') || !q || q === committed){
                return Promise.resolve({ state: 'none' });
            }
            return resolveTyped(q).then(function(r){
                if (searchInput.value.trim() !== q) return { state: 'none' };   // typing moved on
                if (r.state === 'picked') emitCity(r.city);
                else if (r.state === 'ambiguous') renderResults(r.pool.map(function(c){ return { i: c.index }; }));
                else if (r.state === 'unavailable') renderStatus(statusCity, tr('city_load_error', 'Could not load cities — please retry.'), true);
                else renderEmpty();
                if (r.state !== 'picked'){
                    try { resultsBox.scrollIntoView({ block: 'nearest' }); } catch(e){}
                }
                try { if (window.__ae) window.__ae('feature_use', { f: 'place_typed_resolved', tool: opts.tool || '', state: r.state }); } catch(e){}
                return { state: r.state, query: q };
            }, function(){
                renderStatus(statusCity, tr('city_load_error', 'Could not load cities — please retry.'), true);
                return { state: 'unknown', query: q };
            });
        }
    };
}

/* getData exposes the loaded {index, names, normIndex} so the Cities game can
   build its chain index off the SAME dataset+loader (no duplicate fetch). Null
   until loadData() resolves. */
function getData(){ return _data; }

/* search/rowToCity/normalize exported so the profile city picker
   (profile-location.js) can reuse the shared dataset + matcher. */
window.LocationInput = { mount: mount, loadData: loadData, findByName: findByName,
                         search: search, rowToCity: rowToCity, normalize: normalize,
                         nearestByCoords: nearestByCoords, getData: getData,
                         stripPlaceType: stripPlaceType, placeQueryVariants: placeQueryVariants,
                         searchWithFallback: searchWithFallback, resolveTyped: resolveTyped,
                         findByNameWithFallback: findByNameWithFallback,
                         loadRuSupplement: loadRuSupplement };
})();
